import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { AlertController, NavController, ViewWillEnter } from '@ionic/angular';
import { ModalController, ToastController } from '@ionic/angular';
import { Storage } from '@ionic/storage';
import { MessagingService } from '@mobile/app/services/messaging/messaging.service';
import { PedidoService } from '@mobile/app/services/pedido/pedido.service';
import { TaxaEntrega } from '@mobile/core/models/taxa-entrega.model';
import Utils from '@mobile/helpers/utils';
import { ItemDesmembradoPedido } from '@mobile/models/pedido/pedido.model';
import { IAdicional, IBorda, IProduto, IProdutoPizza } from '@mobile/models/produtos/produto.model';
import slugify from 'slugify';

import { EnderecosPage } from '../enderecos/enderecos.page';
import { TotalPartsEnum } from '../enuns/total-parts.enum';
import { FcmService } from '../services/fcm.service';
import { ContadoresFirebaseService } from '../services/firebase/contadores-firebase.service';
import { CuponsFirebaseService } from '../services/firebase/cupons-firebase.service';
import { PedidosFirebaseService } from '../services/firebase/pedidos-firebase.service';
import { StatusFirebaseService } from '../services/firebase/status-firebase.service';
import { TarifasFirebaseService } from '../services/firebase/tarifas-firebase.service';
import { UsersFirebaseService } from '../services/firebase/users-firebase.service';
import { Cupom } from '../shared/types/cupom.type';
import { Local } from '../shared/types/pdv.types';
import { User } from '../shared/types/user.types';
import { UserPage } from '../user/user.page';

type PedidoItem = IProduto & IProdutoPizza & {
  adicionais: IAdicional[];
  borda?: IBorda;
  itens?: IProdutoPizza[];
  qtd: number;
  obs: string;
  tipo?: 'produto' | 'combo';
  comboId?: string;
  itensDesmembrados?: ItemDesmembradoPedido[];
  promocaoId?: string;
  promocaoNome?: string;
  precoOriginal?: number;
  /** Unidades pagas com pontos de fidelidade. */
  qtdPontos?: number;
};

@Component({
  selector: 'app-sacola',
  templateUrl: './sacola.page.html',
  styleUrls: ['./sacola.page.scss'],
})
export class SacolaPage implements OnInit, ViewWillEnter {

  readonly TotalPartsEnum = TotalPartsEnum;
  readonly PONTOS_POR_PIZZA = 10;

  pagamento: any;
  troco: any = null;
  cupom?: Cupom;
  pedido?: PedidoItem[];
  user?: User;
  subtotal: number = 0;
  entrega: boolean = true;
  taxa_entrega: number = 0;
  taxaEntregaClass: TaxaEntrega;
  total: number = 0;
  descontoPontos = 0;
  endereco?: Local;
  disabled = false;
  pontosPedido = 0;
  pedidoPontosAtivo = true;
  /** Nome da promoção aplicada ao sabor (meio a meio, trem ou bi-trem). */
  promocaoDaParte(parte: unknown, totalPartes: number, broto?: boolean): string | undefined {
    const tamanho = broto ? 'broto' : totalPartes === 4 ? 'trem' : totalPartes === 6 ? 'bi-trem' : 'normal';
    return (parte as { promocoes?: Record<string, { nome: string }> }).promocoes?.[tamanho]?.nome;
  }

  lojaAberta$ = this.statusService.getLojaFechada();

  constructor(
    private modalCtrl: ModalController,
    private navCtrl: NavController,
    private router: Router,
    private alertController: AlertController,
    private storage: Storage,
    private pedidosFirebase: PedidosFirebaseService,
    private statusService: StatusFirebaseService,
    private tarifasService: TarifasFirebaseService,
    private cuponsService: CuponsFirebaseService,
    private contadoresService: ContadoresFirebaseService,
    private fcmService: FcmService,
    private messagingService: MessagingService,
    private toastCtrl: ToastController,
    private pedidoService: PedidoService,
    private usersService: UsersFirebaseService,
  ) {
    this.taxaEntregaClass = new TaxaEntrega(this.entrega, 0, 0);
  }

  ngOnInit() {
    this.subscribePedido();
  }

  ionViewWillEnter() {
    this.validaPedidos();
  }

  subscribePedido() {
    this.pedidoService.getPedido().subscribe(
      pedido => {
        this.pedido = pedido;
        this.validaPedidos();
      }
    );
  }

  /**
   * Reseta todos os estados de variaveis da Sacola
   */
  reset() {
    this.pagamento = undefined;
    this.troco = null;
    this.cupom = undefined;
    this.pedido;
    this.user = undefined;
    this.subtotal = 0;
    this.entrega = true;
    this.taxa_entrega = 0;
    this.taxaEntregaClass = new TaxaEntrega(this.entrega, 0, 0);
    this.total = 0;
    this.descontoPontos = 0;
    this.endereco = undefined;
    this.disabled = false;
    this.pontosPedido = 0;
    this.pedidoPontosAtivo = true;
  }

  async validaPedidos() {
    if (this.pedido) {
      this.pedidoPontosAtivo = await this.storage.get('pedidoPontos');
      // removido o carregamento do endereço para que o usuario sempre
      // selecione o endereco que ele vai querer
      // this.endereco = await this.getEndereco();
      // if (!this.endereco) {
      //   this.abrirEndereco();
      //   this.closeModal();
      // }
      this.user = await this.getUsuario();
      this.ajustarPontos();
      this.getTaxaEntrega();
      this.atualizarTotal();

      const localAnonimo = await this.storage.get('endereco_anonimo');
      if (localAnonimo) {
        this.endereco = localAnonimo;
        this.getTaxaEntrega();
        const locais = this.user?.locais || [];

        if (!locais.some(l => l.id === localAnonimo.id)) {
          locais.push(localAnonimo);
          await this.usersService.updateUser(this.user.uid, { locais});
        }

        await this.storage.remove('endereco_anonimo');
      }
    }
  }

  /** Pizzas que o saldo de pontos ainda paga (o saldo só é debitado quando a loja aceita o pedido). */
  get pizzasLivresPontos(): number {
    if (this.pedidoPontosAtivo) {
      return 0;
    }
    const saldo = Math.floor((this.user?.pontos_fidelidade ?? 0) / this.PONTOS_POR_PIZZA);
    return Math.max(0, saldo - this.qtdPizzasPontos);
  }

  get qtdPizzasPontos(): number {
    return (this.pedido ?? []).reduce((soma, item) => soma + (item.qtdPontos ?? 0), 0);
  }

  elegivelPontos(item: PedidoItem): boolean {
    if (!this.user || item.tipo === 'combo' || item.broto) {
      return false;
    }
    if (item.itens) {
      return item.itens.length <= 2 && item.itens.every(parte => parte.promo);
    }
    return !!item.promo;
  }

  alterarPontos(item: PedidoItem, delta: number) {
    const atual = item.qtdPontos ?? 0;
    const novo = Math.max(0, Math.min(item.qtd, atual + this.pizzasLivresPontos, atual + delta));
    if (novo === atual) {
      return;
    }
    if (novo) {
      item.qtdPontos = novo;
    } else {
      delete item.qtdPontos;
    }
    this.pedidoService.setPedido(this.pedido);
    this.atualizarTotal();
  }

  /** Remove o pagamento com pontos que deixou de valer (item removido, quantidade menor ou saldo insuficiente). */
  ajustarPontos() {
    const saldo = this.pedidoPontosAtivo ? 0 : Math.floor((this.user?.pontos_fidelidade ?? 0) / this.PONTOS_POR_PIZZA);
    let restante = saldo;
    let alterou = false;

    for (const item of this.pedido ?? []) {
      const atual = item.qtdPontos ?? 0;
      const novo = this.elegivelPontos(item) ? Math.min(atual, item.qtd, restante) : 0;
      if (novo !== atual) {
        alterou = true;
        if (novo) {
          item.qtdPontos = novo;
        } else {
          delete item.qtdPontos;
        }
      }
      restante -= novo;
    }

    if (alterou) {
      this.pedidoService.setPedido(this.pedido);
    }
  }

  private precoUnitario(item: PedidoItem): number {
    const adicionais = (item.adicionais || []).reduce(
      (price, adc) => price + Utils.getPriceAdicional(adc, item.broto),
      0
    );
    return (item.broto ? item.price_broto : item.price) + adicionais + Utils.getPriceAdicional(item.borda, item.broto);
  }

  valorPontosDoItem(item: PedidoItem): number {
    return Utils.casasDecimais(this.precoUnitario(item) * (item.qtdPontos ?? 0), 2);
  }

  getTaxaEntrega() {
    this.taxaEntregaClass.entrega = this.entrega && !!this.endereco;
    if (this.entrega && this.endereco) {
      this.tarifasService.getTarifa(this.endereco.bairro).then((r) => {
        try {
          this.taxaEntregaClass.taxa_bairro = r[0].valor;
        } catch (e) {
          this.taxaEntregaClass.taxa_bairro = 0;
        }
        this.taxa_entrega = this.taxaEntregaClass.calcTaxaEntrega(
          this.subtotal
        );
        this.atualizarTotal();
      });
    } else {
      // Se não for entrega Zera(0) a taxa de entrega (Balcâo)
      this.taxa_entrega = this.taxaEntregaClass.calcTaxaEntrega(this.subtotal);
    }
  }
  pagamentoDin() {
    this.pagamento = 'dinheiro';
    this.alertTroco('');
    this.atualizarTotal();
  }
  pagamentoCard() {
    this.pagamento = 'card';
    this.atualizarTotal();
  }
  pagamentoPix() {
    this.pagamento = 'pix-entrega';
    this.atualizarTotal();
  }

  atualizarTotal() {
    const subtotal: number = (this.pedido ?? []).reduce(
      (prevVal: number, item: any) => {
        const price_adicionais = (item.adicionais || []).reduce(
          (price, adc) => price + Utils.getPriceAdicional(adc, item.broto),
          0
        );
        const price_borda = Utils.getPriceAdicional(item.borda, item.broto);
        const pizza_price = item.broto ? item.price_broto : item.price;

        prevVal =
          prevVal + (pizza_price + price_adicionais + price_borda) * item.qtd;
        return prevVal;
      },
      0
    );

    //duas casas decimais
    this.descontoPontos = Utils.casasDecimais(
      (this.pedido ?? []).reduce((soma, item) => soma + this.valorPontosDoItem(item), 0),
      2
    );
    this.subtotal = Utils.casasDecimais(subtotal - this.descontoPontos, 2);

    if (this.cupom) {
      switch(this.cupom.type) {
        case 'valor':
          this.subtotal -= Utils.casasDecimais(this.cupom.value, 2);
          break;
        case 'porcentagem':
          this.subtotal = Utils.casasDecimais(
            this.subtotal * ((100 - this.cupom.value) / 100),
            2
          );
        break;
      }
    }

    this.taxa_entrega = this.taxaEntregaClass.calcTaxaEntrega(this.subtotal);

    this.total = Number(this.subtotal) + Number(this.taxa_entrega);
    this.total = parseFloat(this.total.toFixed(2));
  }

  async getUsuario() {
    const user = await this.storage.get('user');
    if (!user) {
      return;
    }

    return await this.usersService.getUser(user['uid']);
  }

  async getPedido() {
    // let pedidoAtual = await this.storage.get('pedido');
    let pedidoAtual = await this.pedidoService.getPedido().toPromise();

    pedidoAtual.forEach((item) => {
      if ((item.type === 'salgada' || item.type === 'doce') && !item.promo) {
        this.pontosPedido += item.qtd;
      }
    });
    return pedidoAtual && pedidoAtual.length > 0 ? pedidoAtual : false;
  }
  async confirmarRemover(index) {
    const alert = await this.alertController.create({
      header: 'Deseja remover o item?',
      buttons: [
        {
          text: 'Não!',
          role: 'cancel',
        }, {
          text: 'Sim!',
          handler: () => {
            this.pedido?.splice(index, 1);
            this.ajustarPontos();
            this.atualizarTotal();
            // this.storage.set('pedido', this.pedido);
            this.pedidoService.setPedido(this.pedido);
          }
        }
      ]
    });
    alert.present();
  }
  async removerItem(id: any) {
    const index = this.pedido.findIndex((item: any) => {
      return item.id === id;
    });
    if (this.pedido[index].qtd === 1) {
      this.confirmarRemover(index);
    } else {
      this.pedido[index].qtd--;
      this.ajustarPontos();
      // this.storage.set('pedido', this.pedido);
      this.pedidoService.setPedido(this.pedido);
      this.atualizarTotal();
    }
  }
  addItem(id: any) {
    const index = this.pedido.findIndex((item: any) => {
      return item.id === id;
    });
    this.pedido[index].qtd++;
    this.atualizarTotal();
    // this.storage.set('pedido', this.pedido);
    this.pedidoService.setPedido(this.pedido);
  }

  async alertTroco(warn) {
    const alert = await this.alertController.create({
      header: 'Precisa de troco?',
      subHeader: warn,
      inputs: [
        {
          name: 'troco',
          type: 'text',
          placeholder: 'Para quanto ?'
        },
      ],
      buttons: [
        {
          text: 'Não preciso',
          handler: () => {
            this.troco = 0;
          }
        }, {
          text: 'Ok',
          handler: (data) => {
            parseFloat(data.troco);
            if (data.troco > this.total) {
              this.troco = parseFloat(data.troco);
            } else {
              this.alertTroco('O valor deve ser maior que o total do pedido.');
            }
          }
        }
      ]
    });
    await alert.present();
    this.troco = 0;
  }
  async inputCupom(msg: Cupom) {

    const alert = await this.alertController.create({
      header: 'Cupom de desconto',
      subHeader: msg ? msg.cupom : '',
      inputs: [
        {
          name: 'cupom',
          type: 'text',
          placeholder: 'Insira aqui'
        },
      ],
      buttons: [
        {
          text: 'Remover',
          role: 'cancel',
          handler: () => {
            this.cupom = undefined;
            this.atualizarTotal();
          }
        }, {
          text: 'Aplicar',
          handler: (data) => {
            const cupom = data.cupom
              .normalize('NFD')
              .replace(/[\u0300-\u036f]/g, '')
              .toLowerCase();
            if (cupom) {
              this.cuponsService
                .getCupom(slugify(cupom))
                .then(async (res) => {
                  if (res) {
                    this.cupom = res[0];
                    console.log(this.cupom);
                    const toast = await this.toastCtrl.create({
                      message: 'Cupom adicionado!',
                      duration: 1000,
                      color: 'secondary',
                      position: 'top',
                    });
                    toast.present();
                    this.atualizarTotal();
                  } else {
                    this.inputCupom({ cupom: 'Cupom não encontrado!' } as any);
                  }
                });
            }
          }
        }
      ]
    });
    await alert.present();
  }

  async validaPedido() {
    if (!this.endereco && this.entrega) {
      this.abrirEndereco();
      return false;
    } else if (!this.pagamento) {
      const toast = await this.toastCtrl.create({
        message: 'Selecione a forma de pagamento!',
        duration: 1200,
        color: 'danger',
        position: 'top',
      });
      toast.present();
    } else if (this.entrega && !this.taxa_entrega) {
      const toast = await this.toastCtrl.create({
        message: 'Problemas para definir a taxa de entrega, cadastre novamente seu endereço.',
        duration: 1500,
        color: 'danger',
        position: 'top',
      });
      toast.present();
    }
    return ((this.endereco && this.entrega) || !this.entrega) &&
      this.pagamento &&
      (!this.entrega || (this.entrega && this.taxa_entrega))
      ? true
      : false;
  }

  /**
   * Acumulador de pontos que o pedido tem
   */
  private pontosDoPedido(itens: any[]): number {

    const PRIMEIRO_NUMERO_PIZZA_ESPECIAIS = 35; // 35 - 4 queijos

    /**
     * Valida se a parte do item do Pedido a "parte" do pedido é da pizza especial
     * @param parte
     * @returns
     */
    const validaParteDoPedido = (parte) => {
      const numero = parseInt(parte.name) || 0;
      return numero >= PRIMEIRO_NUMERO_PIZZA_ESPECIAIS;
    };

    /**
     * Valida se o item do pedido é pizza especial
     * @param pedido
     * @returns
     */
    const validaItemDoPedido = (pedido): boolean => {

      if (pedido.broto) {
        return false;
      }

      if (pedido.itens) {
        return pedido.itens.every(validaParteDoPedido);
      } else {

      }
      const numero = parseInt(pedido.name) || 0;

      return numero >= PRIMEIRO_NUMERO_PIZZA_ESPECIAIS;
    };

    let pontos_acc = 0;

    itens.forEach(itemPedido => {
      if (validaItemDoPedido(itemPedido)) {
        pontos_acc++;
      }
    });

    return pontos_acc ?? 0;
  }

  async autenticarUsuario() {
    if (!this.user) {
      this.navCtrl.navigateRoot(['/auth'], {
        queryParams: {
          redirectUrl:'/tabs/sacola'
        }
      });
      return;
    }
  }

  async confirmarPedido() {

    if (!this.user) {
      this.navCtrl.navigateRoot(['/auth'], {
        queryParams: {
          redirectUrl:'/tabs/sacola'
        }
      });
      return;
    }

    const validaPedido = await this.validaPedido();
    if (validaPedido) {
      this.disabled = true;
      this.atualizarTotal();
      const pizzasComPontos = this.qtdPizzasPontos;
      const descontoPontos = this.descontoPontos;
      const itens = [];
      this.pedido.forEach((item) => {
        // console.log('itens do pedido', item);

        const name = ({ name, broto }) => (broto ? `(BROTO) ${name}` : name);
        let retorno: {
          codigo_interno?: string,
          broto: boolean;
          meioameio: boolean;
          name: string;
          qtd: number;
          price: number;
          obs?: string;
          itens?: { name: string; price: number; promocaoNome?: string }[];
          adicionais?: { name: string; price: number }[];
          borda?: { name: string; price: number };
          produtoId?: string;
          tipo?: 'produto' | 'combo';
          comboId?: string;
          itensDesmembrados?: ItemDesmembradoPedido[];
          promocaoId?: string;
          promocaoNome?: string;
          precoOriginal?: number;
          qtdPontos?: number;
        };

        // tslint:disable-next-line: variable-name
        const price_adicionais = (item.adicionais || []).reduce(
          (price, adc) => price + Utils.getPriceAdicional(adc, item.broto),
          0
        );
        // tslint:disable-next-line: variable-name
        const price_borda = Utils.getPriceAdicional(item.borda, item.broto);

        if (item.meioameio) {
          retorno = {
            codigo_interno: item.codigo_interno ?? null,
            broto: item.broto,
            meioameio: item.meioameio,
            name: item.name,
            qtd: item.qtd,
            price:
              (item.broto ? item.price_broto : item.price) +
              price_adicionais +
              price_borda,
            itens: item.itens!.map((i) => {
              const promocaoNome = this.promocaoDaParte(i, item.itens!.length, item.broto);
              return {
                name: name(i),
                price: item.broto ? item.price_broto : item.price,
                ...(promocaoNome ? { promocaoNome } : {}),
              };
            }),
            adicionais: item.adicionais ? item.adicionais : null,
            borda: item.borda,
          };
        } else {
          retorno = {
            codigo_interno: item.codigo_interno ?? null,
            broto: item.broto || false,
            meioameio: item.meioameio || false,
            name: name(item),
            qtd: item.qtd,
            price: (item.broto ? item.price_broto : item.price) + price_adicionais + price_borda,
            adicionais: item.adicionais ? item.adicionais : null,
            borda: item.borda ? item.borda : null,
          };

          if (item.tipo === 'combo') {
            retorno = { ...retorno, tipo: 'combo', comboId: item.comboId, itensDesmembrados: item.itensDesmembrados };
          } else {
            retorno = { ...retorno, produtoId: item.id };
            if (item.promocaoId) {
              retorno = { ...retorno, promocaoId: item.promocaoId, precoOriginal: item.precoOriginal, ...(item.promocaoNome ? { promocaoNome: item.promocaoNome } : {}) };
            }
          }
        }
        if (item.obs) {
          retorno = {
            ...retorno,
            obs: item.obs,
          };
        }
        if (item.qtdPontos) {
          retorno = { ...retorno, qtdPontos: item.qtdPontos };
        }
        itens.push(retorno);
      });

      const now = new Date();
      const data = now.toLocaleString(['pt-BR'], {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      });
      const horario = now.toLocaleString(['pt-BR'], {
        hour: '2-digit',
        minute: '2-digit',
      });
      let valorSemCupom: any = null;
      let cupomUtilizado = this.cupom ? this.cupom : null;
      if (cupomUtilizado && cupomUtilizado.type === 'valor') {
        valorSemCupom = parseFloat((this.subtotal + cupomUtilizado.value).toFixed(2));
      } else if (cupomUtilizado && this.cupom.type === 'porcentagem') {
        valorSemCupom = parseFloat(
          (this.subtotal / ((100 - cupomUtilizado.value) / 100)).toFixed(2)
        );
      }
      let token;
      try {
        // Token do PushNotifications
        token = this.fcmService.getToken();
      } catch (e) {
        // Token do Service Workers
        const source: Promise<string> = this.messagingService.getToken().toPromise();
        token = await source;
        console.warn('Token do PushNotifications: error', e);
      }

      if (!token) {
        try {
          // Token do Service Workers
          const source: Promise<string> = this.messagingService.getToken().toPromise();
          token = await source;
        } catch (e) {
          console.warn('Token do Service Workers: error', e);
        }
      }

      let enderecoCompleto = 'Like Pizzas - Rua Elias Joaquim das Silva, 309 - Guaruja SP';

      if (this.entrega) {
        enderecoCompleto = `${this.endereco.endereco}, ${this.endereco.bairro}${this.endereco.referencia ? `, Ref: ${this.endereco.referencia}` : ''}`;
      }

      if (!this.user.tel || !this.user.name) {
        const enderecoAtualizado = await this.atualizarEnderecoUsuario();
        if (!enderecoAtualizado) {

          const toast = await this.toastCtrl.create({
            message: 'Atualize seu nome e telefone para finalizar o pedido!',
            duration: 1200,
            color: 'danger',
            position: 'top',
          });
          toast.present();
          return;
        }

        this.user = await this.getUsuario();
      }

      // Organizando dados
      const numeroPedido = await this.contadoresService.getProximoNumeroPedido();
      const pedido = {
        contato: this.user.tel,
        endereco: enderecoCompleto,
        entrega: this.entrega,
        entregador: null,
        criadoEm: now,
        data_pedido: data,
        horario_pedido: horario,
        horario_entrega: null,
        id: (Date.now() + Math.random())
          .toString()
          .replace('.', '')
          .substr(2, 9),
        numero_pedido: numeroPedido,
        origem: 'app',
        nome_usuario: this.user.name,
        pagamento: this.pagamento,
        pedido: itens,
        status: 1,
        qtd_pizza_pontos: pizzasComPontos,
        ...(pizzasComPontos
          ? { pontos_utilizados: pizzasComPontos * this.PONTOS_POR_PIZZA, desconto_pontos: descontoPontos }
          : {}),
        subtotal: this.subtotal,
        cupom: cupomUtilizado,
        subtotal_sem_cupom: valorSemCupom,
        taxa_entrega: this.taxa_entrega,
        total: this.total,
        troco: this.troco,
        user_id: this.user.uid,
        pontos: this.pontosDoPedido(itens), // this.pontosPedido,
        token: token || null,
      };

      // console.log('pedido', pedido)

      // Subindo pedido no firestore
      this.pedidosFirebase.postPedido(pedido).then(() => {
        const pedidos = [];
        this.storage
          .get('pedidoEfetuado')
          .then((res) => {
            if (res) {
              res.forEach((ped) => {
                pedidos.push(ped);
              });
            }
          })
          .then(() => {
            pedidos.push(pedido.id);
            // this.storage.set('pedidoEfetuado', pedidos);
            this.pedidoService.setPedidoEfetuado(pedidos);
            if (pizzasComPontos) {
              this.storage.set('pedidoPontos', true);
            }
          })
          .then(() => {
            this.pedidoService.removePedido();
            // this.closeModal();
            this.router.navigate(['/tabs/status'])
            this.reset();
          });
      });
    }
  }

  calcPriceTotalPedido(pedido): number {
    // tslint:disable-next-line: variable-name
    const price_pedido = pedido.broto ? pedido.price_broto : pedido.price;
    // tslint:disable-next-line: variable-name
    const price_adicionais = (pedido.adicionais || []).reduce(
      (price, adc) => price + Utils.getPriceAdicional(adc, pedido.broto),
      0
    );
    // tslint:disable-next-line: variable-name
    const price_borda = Utils.getPriceAdicional(pedido.borda, pedido.broto);
    return Utils.casasDecimais(
      (price_pedido + price_adicionais + price_borda) * pedido.qtd - this.valorPontosDoItem(pedido),
      2
    );
  }

  async closeModal() {
    await this.modalCtrl.dismiss();
  }
  async abrirEndereco() {
    const modal = await this.modalCtrl.create({
      component: EnderecosPage,
      componentProps: {isModalSelect: true}
    });

    modal.present();
    const { data: endereco } = await modal.onDidDismiss<Local>();
    if (endereco) {
      this.endereco = endereco;
      this.getTaxaEntrega();
    }

  }

  async atualizarEnderecoUsuario() {
    const modal = await this.modalCtrl.create({
      component: UserPage,
      componentProps: { atualizarDados: true },
      swipeToClose: true,
    });

    await modal.present();
    const { role } = await modal.onDidDismiss<Local>();
    if (role === 'atualizado') {
      return true;
    }

    return false;
  }

  goToHome() {
    this.router.navigateByUrl('/tabs/home');
    // this.closeModal();
  }

  calculoPrice(pizza) {
    return Utils.calculoPrice(pizza.price, pizza.Broto);
  }
}
